define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/dom-geometry",
	"underscore",
	"csi/action",
	"csi/def",
	"csi/html",
	"csi/html/Widget",
	"csi/locale",
	"csi/middleware/customView/options/DataSheetCustomViewOptionsStore",
	"csi/middleware/customView/options/DataSheetOptionsTab",
	"csi/prefs",
	"csi/printUtil",
	"csi/reflection",
	"./_CanvasMixin",
], function(declare, domClass, domGeometry, _, csiAction, def, html, HtmlWidget, locale, DataSheetCustomViewOptionsStore, DataSheetOptionsTab, csiPrefs, printUtil, reflection, _CanvasMixin) {

	const CANVAS_VIEW = "TDSSwitchboard";
	const CANVAS_VIEW_KEY = def.composeViewKey(CANVAS_VIEW, "DataSheetRevision");
	const PAGES = "Pages";

	const _canvasWithPropertiesTypes = {
		"Artwork": 1,
		"CollectionColor": 1,
		"ColorDataSheet": 1,
		"ReviewCollection": 1,
		"ReviewColorway": 1,
		"ReviewSKU": 1,
		"ReviewStyle": 1
	};

	csiAction.registerControllers("DataSheet", {
		CustomViewManage: "csi/datasheet/DataSheetCustomViewController",
		CustomViewSaveAs: "csi/datasheet/DataSheetCustomViewController",
	});

	return declare([HtmlWidget, _CanvasMixin], {
		bPrintView: true,
		splitterType: "datasheet",

		/** @property {boolean} applyPrintSize - set to true for view to be constrained by paper size and orientation */
		applyPrintSize: false,

		// DOMNode: node holding view before canvas pages
		//_beforeCanvasNode: null,

		customViewOptionsComponent: DataSheetOptionsTab,
		customViewOptionsStore: DataSheetCustomViewOptionsStore,

		/**
		 * @override
		 * Assume datasheet views do not have any child views exceeding the limit. They must be able to print.
		 */
		doesQueryTotalExceedMax() {
			return !this._started;
		},

		getNestedViewStore: function() {
			// summary: return store for nested non-slim views - include entry for Canvas if it is shown on another tab
			var store = this.inherited(arguments);
			var canvasView = this.getChildView(CANVAS_VIEW);
			if (canvasView) {
				// canvas view uses custom views of parent TDS to assign proper paper and orientation, but does not allow new custom views for itself;
				// so, reset setting that allows for specifying custom view in datasheet
				store.get(canvasView.getViewPreferenceContext()).store = null;
			} else if (this._hasPages()) {
				store.add({
					id: PAGES,
					name: locale.typeId(this.getViewNodeType(), PAGES),
					view: "",
					visible: false
				});
			}

			return store;
		},

		/**
		 * @override
		 * Remove custom view options if not supported
		 */
		onCreateDefaultCustomView(cv) {
			this.inherited(arguments);

			const rc = this.defView.getRootClass();
			const rcMaster = rc?.getMasterClass();

			if (!_canvasWithPropertiesTypes[rcMaster?.type]) {
				cv.removeCustomOption("canvasWithProperties");
				cv.removeCustomOption("canvasWithPropertiesHeight");
			}
		},

		/**
		 * @override
		 * Apply print size to view if so configured
		 */
		onViewCreate: function() {
			if (this.applyPrintSize) {
				var rootNode = this.getContentRoot();
				var pageSize = printUtil.getPageSizeForSettings(this.getPrintPaper(), this.getPrintOrientation());
				$(rootNode).width(pageSize.w);
			}

			this.inherited(arguments);
		},

		_hasPages: function() {
			// summary: return true if root node of view has Pages attribute (Theme/DataSheetRevision);
			// do not use csi/def/View#getRootClass since this is called before that is set
			var rc = reflection.getNodeClass(this.getViewNode()) || this.defView.getRootClass();
			return !!reflection.getClassAttribute(rc, PAGES);
		},

		_hasToolbar: function() {
			// summary: show toolbar for all data sheets
			return true;
		},

		_postView: function() {
			// summary: combine first canvas page with previous page if so configured
			var beforeCanvasNode = this._beforeCanvasNode;
			if (beforeCanvasNode && beforeCanvasNode.nextSibling && this.getCanvasCombineWithProperties()) {
				var h = this.getCanvasCombineWithPropertiesHeight();
				var propertiesView = this.getChildView("Properties");
				var propDomNode = propertiesView && propertiesView.domNode;

				if (propDomNode && (propDomNode.parentNode == beforeCanvasNode)) {
					domClass.add(propDomNode, "csi-property-view-with-canvas");

					var cb = domGeometry.getContentBox(propDomNode);
					if (cb.h <= h) {
						propertiesView.set("splitterType", null);
						domClass.remove(propDomNode.parentNode, html.CSS_PAGE);
						html.setPixels(propDomNode, html.HEIGHT, h);
					} else {
						domClass.remove(propDomNode, "csi-property-view-with-canvas");
					}
				}
			}

			// fix height of canvas pages based on height of titleblock
			this.postRenderCanvasPages();

			this.set("disabled", false);

			this.inherited(arguments);
		},

		_processHtml: function(/*Context*/ctx, /*DOMNode*/domNode) {
			// summary: automatically populate contentNode based on number of ViewRefs if template not specified
			if (domNode && !this.htmlTemplate) {
				// clear all content before creating new content
				html.clear(domNode, true);

				// add page block for each ViewRef - first ViewRef should be TitleBlock that will be repeated for each print page
				var root = html.create("div", {"class": "csi-datasheet"});
				var keyAttr = html.getDataAttributeName(html.DATA_TEMPLATE_KEY);
				var labelAttr = html.getDataAttributeName(html.DATA_TEMPLATE_LABEL);

				const defView = this.defView;
				const repeatFirstView = defView.repeatFirstView;
				const minLabelIndex = repeatFirstView ? 1 : 0;

				_.each(defView.m_defs, function(obj, i) {
					if (obj.isViewRef && (!ctx.bPrint || (obj.key != CANVAS_VIEW))) {
						var params = {};
						params[keyAttr] = obj.key;

						if (i || (repeatFirstView === false)) {
							params["class"] = html.CSS_PAGE;
						}

						if (i > minLabelIndex) {
							params[labelAttr] = "";
						}

						html.create("div", params, root);
					}
				}, this);

				domNode.appendChild(root);

				if (ctx.bPrint && this._hasPages()) {
					// output canvas pages when printing
					var node = ctx.getNode();
					var pages = ctx.cache.getNodes(node.Pages);

					if (pages.length) {
						// do not output canvas pages if custom view does not include it
						var canvasKey = csiPrefs.composeKey(CANVAS_VIEW_KEY, defView.getId());

						if (!this.nestedViews || this.nestedViews[PAGES] || this.nestedViews[canvasKey]) {
							// render view for each DataSheetPage, tracking the nodes created for each
							this._beforeCanvasNode = root.lastChild;
							this.migrateCanvasSplits(pages);
							this.renderCanvasPages(ctx, pages, root);
						}
					}
				}
			}

			this.inherited(arguments);
		},

		_processKey: function(/*Context*/ctx, /*String*/key, /*Object*/def, /*DOMNode*/domNode) {
			// summary: skip canvas view when printing - pages are added individually above
			if (ctx.bPrint && (key == CANVAS_VIEW)) {
				html.destroyElement(domNode);
				return null;
			}

			return this.inherited(arguments);
		},

		_onRootDependencyPaths: function(/*String[]*/paths) {
			// summary: add own paths - Headings and Images are already taken care of, so skip them
			if (this._hasPages()) {
				paths.push("Child:Pages/Child:Comments/Child:Author");
				paths.push("Child:Pages/Child:Snapshot");
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Return dropdown options for datasheet custom view
		 */
		getCustomViewDropDownOptions() {
			// Canvas notes orientation dropdown options
			const canvasCommentsOrientations = [
				{id: "", res: "ViewPreferences_DefaultValue", value: ""},
				{id: "left", res: "LeftRight", value: "left"},
				{id: "top", res: "TopBottom", value: "top"},
			];

			// Paper size and paper orientation dropdown options
			const options = this.inherited(arguments);

			return {
				...options,
				canvasCommentsOrientations,
			};
		},
	});
});